import {
  AdminPanelSettings as AdminIcon,
  Business as BusinessIcon,
  ShoppingCart as CartIcon,
  Business as CompaniesIcon,
  CreditCard as CreditCardIcon,
  Dashboard as DashboardIcon,
  Star as LoyaltyIcon,
  Storefront as MarketplaceIcon,
  ShoppingCart as OrdersIcon,
  TrendingUp as PerksIcon,
  Build as ServicesIcon,
  Settings as SettingsIcon,
  Share as ShareIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { BottomNavigation, BottomNavigationAction, Paper, useTheme } from '@mui/material';
import React from 'react';

import { featureFlags } from '../../lib/feature-flags';

interface BottomNavigationProps {
  role: string;
  activePath: string;
  onNavigate: (path: string) => void;
}

interface TabItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  roles: string[];
  featureFlag?: string;
}

const BottomNavigationComponent: React.FC<BottomNavigationProps> = ({
  role,
  activePath,
  onNavigate,
}) => {
  const theme = useTheme();

  // Detect catalog context
  const isCatalogContext = activePath.startsWith('/c/');
  const catalogSlug = isCatalogContext ? activePath.split('/')[2] : null;

  // Define tabs for each role - keeping it to 4-5 max per role
  const baseTabConfigurations: Record<string, TabItem[]> = {
    customer: [
      {
        label: isCatalogContext ? catalogSlug || 'Catalog' : 'Marketplace',
        icon: <MarketplaceIcon />,
        path: '/',
        roles: ['customer'],
      },
      {
        label: 'Cart',
        icon: <CartIcon />,
        path: '/cart',
        roles: ['customer'],
        featureFlag: 'customer-cart-tab',
      },
      { label: 'Orders', icon: <OrdersIcon />, path: '/orders', roles: ['customer'] },
      { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['customer'] },
      { label: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['customer'] },
    ],
    partner_admin: [
      { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['partner_admin'] },
      { label: 'Orders', icon: <OrdersIcon />, path: '/partners/orders', roles: ['partner_admin'] },
      { label: 'Fulfillments', icon: <OrdersIcon />, path: '/partners/fulfillments', roles: ['partner_admin'] },
      {
        label: 'Perks Strategy',
        icon: <PerksIcon />,
        path: '/partners/perks/marketplace',
        roles: ['partner_admin'],
      },
      { label: 'Services', icon: <SettingsIcon />, path: '/settings', roles: ['partner_admin'] },
      {
        label: 'Brand Management',
        icon: <BusinessIcon />,
        path: '/partners/profile',
        roles: ['partner_admin'],
      },
    ],
    partner_staff: [
      { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['partner_staff'] },
      { label: 'Orders', icon: <OrdersIcon />, path: '/partners/orders', roles: ['partner_staff'] },
      { label: 'Fulfillments', icon: <OrdersIcon />, path: '/partners/fulfillments', roles: ['partner_staff'] },
      { label: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['partner_staff'] },
    ],
    admin: [
      {
        label: 'Dashboard',
        icon: <DashboardIcon />,
        path: '/dashboard',
        roles: ['admin', 'super_admin'],
      },
      {
        label: 'Services',
        icon: <ServicesIcon />,
        path: '/services',
        roles: ['admin', 'super_admin'],
      },
      { label: 'Admin', icon: <AdminIcon />, path: '/admin', roles: ['admin', 'super_admin'] },
      {
        label: 'Settings',
        icon: <SettingsIcon />,
        path: '/settings',
        roles: ['admin', 'super_admin'],
      },
    ],
    super_admin: [
      { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['super_admin'] },
      { label: 'Orders', icon: <OrdersIcon />, path: '/admin/orders', roles: ['super_admin'] },
      {
        label: 'Referrals',
        icon: <ShareIcon />,
        path: '/super-admin/referrals',
        roles: ['super_admin'],
      },
      {
        label: 'Loyalty',
        icon: <LoyaltyIcon />,
        path: '/admin/loyalty',
        roles: ['super_admin'],
      },
      {
        label: 'Flagship Services',
        icon: <ServicesIcon />,
        path: '/admin/flagship-services',
        roles: ['super_admin'],
      },
      {
        label: 'Partner Balances',
        icon: <AdminIcon />,
        path: '/super-admin/balances',
        roles: ['super_admin'],
      },
      {
        label: 'Billing Management',
        icon: <CreditCardIcon />,
        path: '/super-admin/billing',
        roles: ['super_admin'],
      },
      {
        label: 'Companies',
        icon: <CompaniesIcon />,
        path: '/super-admin/companies',
        roles: ['super_admin'],
      },
      {
        label: 'Fulfillments',
        icon: <OrdersIcon />,
        path: '/super-admin/fulfillments',
        roles: ['super_admin'],
      },
      {
        label: 'AI Agents',
        icon: <AutoAwesomeIcon />,
        path: '/super-admin/agents',
        roles: ['super_admin'],
      },
      { label: 'Settings', icon: <SettingsIcon />, path: '/settings', roles: ['super_admin'] },
    ],
  };

  // Get tabs for current role and filter by feature flags
  const allTabs = baseTabConfigurations[role] || baseTabConfigurations.customer;
  const tabs = allTabs
    .filter((tab) => !tab.featureFlag || featureFlags.isEnabled(tab.featureFlag))
    .map((tab) => {
      let path = tab.path;

      if (isCatalogContext && catalogSlug) {
        if (tab.path === '/') {
          // Marketplace tab in catalog context goes to catalog root
          path = `/c/${catalogSlug}`;
        } else {
          // Other tabs get catalog prefix
          path = `/c/${catalogSlug}${tab.path}`;
        }
      }

      return { ...tab, path };
    });

  // Find active tab index - handle catalog paths
  const activeIndex = tabs.findIndex((tab) => {
    if (isCatalogContext) {
      // For catalog marketplace page (e.g., /c/alpha), only marketplace tab is active
      if (activePath === `/c/${catalogSlug}`) {
        return tab.path === `/c/${catalogSlug}`;
      }
      // For other catalog pages (e.g., /c/alpha/cart), match exact path
      return activePath === tab.path;
    } else {
      // For regular paths, match as before
      return activePath === tab.path || activePath.startsWith(tab.path + '/');
    }
  });

  // Handle navigation
  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const selectedTab = tabs[newValue];
    if (selectedTab) {
      onNavigate(selectedTab.path);
    }
  };

  // Show on all screen sizes for consistent UX
  // Bottom navigation works well across all devices

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
      elevation={3}
    >
      <BottomNavigation
        value={activeIndex >= 0 ? activeIndex : false}
        onChange={handleChange}
        showLabels
        sx={{
          height: 56,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            maxWidth: 'none',
            padding: '6px 0',
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          },
        }}
      >
        {tabs.map((tab, index) => (
          <BottomNavigationAction
            key={tab.path}
            label={tab.label}
            icon={tab.icon}
            sx={{
              '& .MuiBottomNavigationAction-label': {
                fontSize: '0.75rem',
                fontWeight: activeIndex === index ? 600 : 400,
                transition: 'all 0.2s ease',
              },
            }}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};

export default BottomNavigationComponent;
